/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import { Popover } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  calendarEntryKey,
  interleaveByStart,
} from '@sportninja/common/features/event/calendarItems'
import type { CalendarEntry } from '@sportninja/common/features/event/calendarItems'

import {
  font,
  weightBold,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'
import type { CalendarDayCellProps } from './types'
import type { Game } from '../game-list-row/types'
import CalendarGameCard from './CalendarGameCard'
import CalendarEventCard from './CalendarEventCard'
import { calendarLabel } from './calendarI18n'

const MAX_VISIBLE = 3

/**
 * antd paints the popover's inner surface itself, and `overlayInnerStyle` takes
 * a plain style object — there is no class seam to hand emotion. Hoisted so the
 * object is not rebuilt on every render of every day cell in the month.
 */
const POPOVER_INNER_STYLE = {
  background: colors.SECONDARY_400,
  border: `1px solid ${colors.SECONDARY_200}`,
  padding: 8,
  width: 296,
  maxWidth: 296,
  boxSizing: 'border-box',
  overflow: 'hidden',
} as const

const CalendarDayCell = ({
  day,
  isSoccer,
  isHockey,
  maxVisibleGames = MAX_VISIBLE,
}: CalendarDayCellProps) => {
  'use memo'

  const [popoverOpen, setPopoverOpen] = useState(false)

  //  TBD games come first: they are the ones that still need a decision, and
  //  sorting them among timed games would either bury them or fake a 23:58 slot.
  //  Timed games and events then interleave by start time — the same rule the
  //  list view applies, so a cell and a day section agree on their order.
  const ordered: CalendarEntry[] = [
    ...day.tbdGames.map((game) => ({ kind: 'game' as const, game })),
    ...interleaveByStart(day.games, day.events),
  ]
  const visibleEntries = ordered.slice(0, maxVisibleGames)
  const hiddenCount = ordered.length - maxVisibleGames
  const hasMore = hiddenCount > 0

  const renderEntry = (entry: CalendarEntry) =>
    entry.kind === 'event' ? (
      <CalendarEventCard key={calendarEntryKey(entry)} event={entry.event} />
    ) : (
      <CalendarGameCard
        key={calendarEntryKey(entry)}
        game={entry.game as Game}
        isSoccer={isSoccer}
        isHockey={isHockey}
      />
    )

  return (
    <div css={cellStyles(day.isCurrentMonth)}>
      <div css={dayNumberStyles}>
        {day.isToday ? (
          <span css={todayBadgeStyles}>{day.dayNumber}</span>
        ) : (
          day.dayNumber
        )}
      </div>

      {visibleEntries.map(renderEntry)}

      {hasMore && (
        <Popover
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          trigger='click'
          placement='bottom'
          getPopupContainer={(trigger) =>
            (trigger.closest('[data-calendar-grid]') as HTMLElement) ||
            document.body
          }
          content={
            <div css={popoverContentStyles}>{ordered.map(renderEntry)}</div>
          }
          overlayInnerStyle={POPOVER_INNER_STYLE}
        >
          <button type='button' css={moreButtonStyles}>
            {calendarLabel('calendarMoreGames', { count: hiddenCount })}
          </button>
        </Popover>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const cellStyles = (isCurrentMonth: boolean) => css`
  min-height: 120px;
  min-width: 0;
  padding: 6px;
  border-right: 1px solid ${colors.SECONDARY_200};
  border-bottom: 1px solid ${colors.SECONDARY_200};
  background: ${isCurrentMonth ? colors.SECONDARY_500 : colors.SECONDARY_600};
  opacity: ${isCurrentMonth ? 1 : 0.4};
  display: flex;
  flex-direction: column;
  gap: 3px;
  position: relative;
  overflow: hidden;

  &:nth-of-type(7n) {
    border-right: none;
  }
`

const dayNumberStyles = css`
  ${font.body}
  ${weightMedium}
  font-size: 14px;
  color: ${colors.NEUTRAL_50};
  line-height: 1;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`

const todayBadgeStyles = css`
  ${weightBold}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
  font-size: 14px;
`

const moreButtonStyles = css`
  ${font.body}
  ${weightSemiBold}
  font-size: 13px;
  color: ${colors.PRIMARY};
  background: transparent;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 1px;
  }
`

const popoverContentStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
`

export default CalendarDayCell
